import React, { useEffect } from 'react';
import { Info, Globe, Shield, Mail, ExternalLink, Heart, Users, BookOpen, Vibrate, Moon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

export default function About({ onClose, vibrationEnabled, onVibrationToggle, darkMode, onDarkModeToggle }) {
  const { t } = useTranslation();
  const appVersion = '1.0.16';
  const isNative = Capacitor.isNativePlatform();

  // Lock body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    const scrollY = window.scrollY;
    
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;
    
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    };
  }, []);
  
  const openPrivacyPolicy = async () => {
    try {
      await Browser.open({ url: 'https://eat-halal-food.web.app/privacy-policy.html' });
    } catch (error) {
      // Fallback to regular link if Browser plugin fails
      window.open('https://eat-halal-food.web.app/privacy-policy.html', '_blank');
    }
  };
  
  return (
    <div className="about-overlay" onClick={onClose}>
      <div className="about-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
            zIndex: 10
          }}
        >
          <X size={20} />
        </button>
        
        <div className="about-header">
          <img 
            src="/assets/icon.png" 
            alt="PureBite Halal" 
            className="about-logo"
          />
          <h2>PureBite Halal</h2>
          <p className="about-version">Version {appVersion}</p>
        </div>

        <div className="about-content">
          <section className="about-section">
            <div className="about-section-header">
              <Info size={20} />
              <h3>{t('about.aboutTitle')}</h3>
            </div>
            <p>{t('about.aboutText')}</p>
          </section>

          <section className="about-section">
            <div className="about-section-header">
              <BookOpen size={20} />
              <h3>{t('about.dataSourcesTitle')}</h3>
            </div>
            <ul className="about-list">
              <li>
                <strong>Open Food Facts</strong>
                <a 
                  href="https://world.openfoodfacts.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="about-link"
                >
                  openfoodfacts.org <ExternalLink size={14} />
                </a>
              </li>
              <li>
                <strong>Spoonacular API</strong>
                <a 
                  href="https://spoonacular.com/food-api" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="about-link"
                >
                  spoonacular.com <ExternalLink size={14} />
                </a>
              </li>
            </ul>
          </section>

          <section className="about-section">
            <div className="about-section-header">
              <Shield size={20} />
              <h3>{t('about.privacyTitle')}</h3>
            </div>
            <div className="privacy-highlights">
              <p><strong>{t('about.privacyLocalStorage')}:</strong> {t('about.privacyLocalStorageDesc')}</p>
              <p><strong>{t('about.privacyCamera')}:</strong> {t('about.privacyCameraDesc')}</p>
              <p><strong>{t('about.privacyThirdParty')}:</strong> {t('about.privacyThirdPartyDesc')}</p>
              <p><strong>{t('about.privacyRights')}:</strong> {t('about.privacyRightsDesc')}</p>
            </div>
            <button 
              onClick={openPrivacyPolicy}
              className="about-action-button"
              style={{ cursor: 'pointer' }}
            >
              {t('about.readPrivacyPolicy')} <ExternalLink size={16} />
            </button>
          </section>

          {/* Settings Section - Vibration Toggle */}
          {isNative && (
            <section className="about-section">
              <div className="about-section-header">
                <Vibrate size={20} />
                <h3>{t('about.settingsTitle', { defaultValue: 'Settings' })}</h3>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '12px 0'
              }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
                  {t('about.vibrationLabel', { defaultValue: 'Haptic Feedback (Vibration)' })}
                </span>
                <button
                  onClick={() => onVibrationToggle?.(!vibrationEnabled)}
                  style={{
                    width: '52px',
                    height: '28px',
                    borderRadius: '14px',
                    border: 'none',
                    background: vibrationEnabled ? '#059669' : 'var(--bg-tertiary)',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: '2px',
                    left: vibrationEnabled ? '26px' : '2px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '12px',
                    background: 'white',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    transition: 'left 0.2s'
                  }} />
                </button>
              </div>
              <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', margin: 0 }}>
                {t('about.vibrationDesc', { defaultValue: 'Vibrate when scanning barcodes and detecting halal/haram status' })}
              </p>
            </section>
          )}

          {/* Dark Mode Toggle */}
          <section className="about-section">
            <div className="about-section-header">
              <Moon size={20} />
              <h3>{t('about.appearanceTitle', { defaultValue: 'Appearance' })}</h3>
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              padding: '12px 0'
            }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
                {t('about.darkModeLabel', { defaultValue: 'Dark Mode' })}
              </span>
              <button
                onClick={() => onDarkModeToggle?.(!darkMode)}
                style={{
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  border: 'none',
                  background: darkMode ? '#059669' : 'var(--bg-tertiary)',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
              >
                <span style={{
                  position: 'absolute',
                  top: '2px',
                  left: darkMode ? '26px' : '2px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '12px',
                  background: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  transition: 'left 0.2s'
                }} />
              </button>
            </div>
            <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', margin: 0 }}>
              {t('about.darkModeDesc', { defaultValue: 'Switch between light and dark theme' })}
            </p>
          </section>

          <section className="about-section">
            <div className="about-section-header">
              <Heart size={20} />
              <h3>{t('about.acknowledgmentsTitle')}</h3>
            </div>
            <p>{t('about.acknowledgmentsText')}</p>
          </section>

          <section className="about-section">
            <div className="about-section-header">
              <Users size={20} />
              <h3>{t('about.communityTitle')}</h3>
            </div>
            <p>{t('about.communityText')}</p>
          </section>

          <section className="about-section">
            <div className="about-section-header">
              <Mail size={20} />
              <h3>{t('about.contactTitle')}</h3>
            </div>
            <p>{t('about.contactText')}</p>
            <a 
              href="mailto:purebitehalal@gmail.com"
              className="about-action-button"
            >
              {t('about.contactEmail')}
            </a>
          </section>

          <section className="about-section disclaimer">
            <div className="about-section-header">
              <Globe size={20} />
              <h3>{t('about.disclaimerTitle')}</h3>
            </div>
            <p className="disclaimer-text">{t('about.disclaimerText')}</p>
          </section>
        </div>

        <button className="about-close-btn" onClick={onClose}>
          {t('about.close')}
        </button>
      </div>
    </div>
  );
}
